import React, { forwardRef, useCallback, useEffect, useRef } from "react";
import "./Input.scss";

// Fontaine M17 — Textarea: auto-grows with content up to maxRows, optional character counter.

/**
 * @param {number} [minRows=2]
 * @param {number} [maxRows=8]          past this it scrolls
 * @param {number} [maxLength]          shows "n / max" counter (turns danger when over)
 * @param {boolean} [invalid]
 * @param {boolean} [fullWidth=true]
 */
const Textarea = forwardRef(function Textarea(
  { minRows = 2, maxRows = 8, maxLength, invalid = false, fullWidth = true, className = "", value, defaultValue, onChange, disabled = false, ...rest },
  ref
) {
  const inner = useRef(null);
  const setRefs = (el) => {
    inner.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };

  const fit = useCallback(() => {
    const el = inner.current;
    if (!el) return;
    const cs = window.getComputedStyle(el);
    const line = parseFloat(cs.lineHeight) || 22;
    const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0) + (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    el.style.height = "auto";
    const max = line * maxRows + pad;
    const min = line * minRows + pad;
    const next = Math.min(Math.max(el.scrollHeight, min), max);
    el.style.height = `${next}px`;
    el.classList.toggle("is-scroll", el.scrollHeight > max);
  }, [minRows, maxRows]);

  useEffect(() => { fit(); }, [fit, value]);

  const length = (value ?? inner.current?.value ?? defaultValue ?? "").length;
  const over = maxLength != null && length > maxLength;
  const classes = ["ft-textarea", fullWidth ? "ft-textarea--full" : "", invalid || over ? "is-invalid" : "", className].filter(Boolean).join(" ");

  return (
    <div className={classes}>
      <textarea
        ref={setRefs}
        className="ft-textarea__control"
        rows={minRows}
        value={value}
        defaultValue={defaultValue}
        disabled={disabled}
        aria-invalid={invalid || over || undefined}
        onChange={(e) => { onChange?.(e); if (value === undefined) fit(); }}
        {...rest}
      />
      {maxLength != null ? (
        <span className={`ft-textarea__counter${over ? " is-over" : ""}`} aria-live="polite">
          {length} / {maxLength}
        </span>
      ) : null}
    </div>
  );
});

export default Textarea;
